import { TABLE_V2_LAYOUT_ID } from '../layout-id.js';
import { type TableGeometryPort } from '../model/table-geometry.js';
import { type TableModel } from '../model/table-model.js';

const TABLE_V2_ROOT_SELECTOR = `[data-dbs-layout="${TABLE_V2_LAYOUT_ID}"]`;

/**
 * Renderer-owned regions. Data-column indices exclude the optional prefix.
 * Frozen width is derived from these same resolved columns, never measured again.
 *
 * Hosts live for the renderer generation; strategies own only their children.
 * React renders each host empty and publishes its element after commit. Absolute
 * positioning excludes overlays from content measurement; pointer transparency
 * belongs to the host, while a feature may opt its own control into hit testing.
 * Each data host starts at its first column and contains feature z-indices.
 *
 * A nonzero frozen count requires a frozenBody host (and frozenHeader when the
 * header exists). Publish matching tracks and hosts together before redraw.
 */
export interface TableV2HighlightLayout {
  readonly columns: readonly { readonly start: number; readonly width: number }[];
  readonly frozenLeadingColumnCount: number;
  readonly prefixWidth: number;
  readonly headerHeight: number;
  readonly body: HTMLElement;
  readonly header?: HTMLElement;
  readonly prefix?: HTMLElement;
  readonly corner?: HTMLElement;
  readonly frozenBody?: HTMLElement;
  readonly frozenHeader?: HTMLElement;
}

/**
 * One committed renderer generation. Geometry is body-relative; the cached origin
 * translates it into the current scroll owner's content coordinates. Only the
 * renderer owns virtualizers, dimensions, and mount/disposal of this bridge.
 */
export interface TableV2GeometryTarget {
  readonly model: TableModel;
  readonly highlights?: TableV2HighlightLayout;
  readonly available: boolean;
  readonly geometry: TableGeometryPort;
  readonly scrollContainer: HTMLElement;
  readonly origin: { readonly left: number; readonly top: number };
  readonly totalWidth: number;
  readonly totalHeight: number;
  scrollToCell(row: number, column: number): void;
}

const targets = new WeakMap<HTMLElement, TableV2GeometryTarget>();

/**
 * Cleanup removes only its own generation and releases the previous owner.
 */
export function registerTableV2GeometryTarget(
  root: HTMLElement,
  target: TableV2GeometryTarget
): VoidFunction {
  targets.set(root, target);

  return () => {
    if (targets.get(root) === target) {
      targets.delete(root);
    }
  };
}

/**
 * A detached or replaced renderer has no geometry, regardless of old cached DOM.
 */
export function getTableV2GeometryTarget(
  container: HTMLElement
): TableV2GeometryTarget | undefined {
  const root = container.matches(TABLE_V2_ROOT_SELECTOR)
    ? container
    : container.querySelector<HTMLElement>(TABLE_V2_ROOT_SELECTOR);

  if (!root?.isConnected) {
    return undefined;
  }

  const target = targets.get(root);

  return target?.scrollContainer === container ? target : undefined;
}

/**
 * Reveals a logical target even when both virtual windows exclude it.
 */
export function scrollTableV2CellIntoView(
  container: HTMLElement,
  row: number,
  column: number
): void {
  getTableV2GeometryTarget(container)?.scrollToCell(row, column);
}
